// src/services/technicalReportService.js
import { db, storage } from '../config/firebase';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, getDoc, query, orderBy, serverTimestamp, runTransaction } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const COLLECTION = 'technicalReports';

export const technicalReportService = {

    // Obtener todos los informes técnicos
    async getAll() {
        const q = query(collection(db, COLLECTION), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    },

    // Obtener uno por ID
    async getById(id) {
        const snap = await getDoc(doc(db, COLLECTION, id));
        return snap.exists() ? { id: snap.id, ...snap.data() } : null;
    },

    // Guardar (crear o actualizar)
    async save({ reportId, formData, photos, userId }) {
        // Subir fotos nuevas
        const uploadedPhotos = [];
        for (const photo of (photos?.newFiles || [])) {
            const storageRef = ref(storage, `technicalReports/${Date.now()}_${photo.name}`);
            await uploadBytes(storageRef, photo);
            const url = await getDownloadURL(storageRef);
            uploadedPhotos.push({ url, name: photo.name, caption: photo.caption || '', description: photo.description || '' });
        }

        // Combinar fotos existentes + nuevas
        const allPhotos = [...(photos?.existingInfos || []), ...uploadedPhotos];

        const payload = {
            ...formData,
            fotos: allPhotos,
            updatedAt: serverTimestamp(),
            updatedBy: userId
        };

        // Subir fotos específicas de TE1 (Certificados y fotos de circuitos)
        if (payload.type === 'verificacion_inicial') {
            if (payload.instrumentos) {
                for (let i = 0; i < payload.instrumentos.length; i++) {
                    const inst = payload.instrumentos[i];
                    if (inst.file) {
                        const storageRef = ref(storage, `technicalReports/certs/${Date.now()}_${inst.file.name}`);
                        await uploadBytes(storageRef, inst.file);
                        inst.certUrl = await getDownloadURL(storageRef);
                        delete inst.file; 
                        delete inst.preview;
                    }
                }
            }
            if (payload.circuitos) {
                for (let i = 0; i < payload.circuitos.length; i++) {
                    const circ = payload.circuitos[i];
                    if (circ.file) {
                        const storageRef = ref(storage, `technicalReports/circuits/${Date.now()}_${circ.file.name}`);
                        await uploadBytes(storageRef, circ.file);
                        circ.fotoUrl = await getDownloadURL(storageRef);
                        delete circ.file;
                        delete circ.preview;
                    }
                }
            }
            if (payload.tableros) {
                for (let i = 0; i < payload.tableros.length; i++) {
                    const tab = payload.tableros[i];
                    if (tab.file) {
                        const storageRef = ref(storage, `technicalReports/tableros/${Date.now()}_${tab.file.name}`);
                        await uploadBytes(storageRef, tab.file);
                        tab.fotoUrl = await getDownloadURL(storageRef);
                        delete tab.file;
                        delete tab.preview;
                    }
                }
            }
        }

        if (reportId) {
            // Actualizar (no cambia el folio)
            await updateDoc(doc(db, COLLECTION, reportId), payload);
            return reportId;
        } else {
            // Crear — generar folio secuencial
            const counterRef = doc(db, 'counters', 'techReportCounter');
            const folio = await runTransaction(db, async (t) => {
                const snap = await t.get(counterRef);
                const next = snap.exists() ? snap.data().current + 1 : 1;
                t.set(counterRef, { current: next }, { merge: true });
                return String(next).padStart(7, '0');
            });
            payload.folio = folio;
            payload.createdAt = serverTimestamp();
            payload.createdBy = userId;
            const docRef = await addDoc(collection(db, COLLECTION), payload);
            return docRef.id;
        }
    },

    // Eliminar
    async delete(id) {
        const report = await this.getById(id);
        if (report?.fotos) {
            for (const foto of report.fotos) {
                try {
                    if (foto.url && foto.url.includes('firebase')) {
                        const photoRef = ref(storage, foto.url);
                        await deleteObject(photoRef);
                    }
                } catch (e) { /* foto ya borrada */ }
            }
        }
        await deleteDoc(doc(db, COLLECTION, id));
    },

    // Helper: obtener datos iniciales (clientes, parámetros)
    async getInitialData() {
        const [clientsSnap, paramsSnap] = await Promise.all([
            getDocs(collection(db, 'clients')),
            getDoc(doc(db, 'parameters', 'system-settings'))
        ]);
        return {
            clients: clientsSnap.docs.map(d => ({ id: d.id, ...d.data() })),
            parameters: paramsSnap.exists() ? paramsSnap.data() : {}
        };
    }
};
